import React from "react";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Upload } from "lucide-react";

export default function AdminPackages() {
    const [items, setItems] = React.useState([]);
    const [products, setProducts] = React.useState([]);
    const [editing, setEditing] = React.useState(null);

    const load = React.useCallback(async () => {
        const [a, b] = await Promise.all([api.get("/admin/packages"), api.get("/admin/products")]);
        setItems(a.data || []); setProducts(b.data || []);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            if (editing.package_id) await api.put(`/admin/packages/${editing.package_id}`, editing);
            else await api.post("/admin/packages", editing);
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    const del = async (id) => { if (!window.confirm("Sigur ștergi?")) return; await api.delete(`/admin/packages/${id}`); toast.success("Șters"); load(); };

    const addItem = () => setEditing(p => ({ ...p, items: [...(p.items || []), { product_id: products[0]?.product_id || "", quantity: 1 }] }));
    const updateItem = (i, k, v) => setEditing(p => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
    const removeItem = (i) => setEditing(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

    const uploadImg = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(p => ({ ...p, image: res.data.url }));
    };

    return (
        <div data-testid="admin-packages">
            <div className="flex items-center justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Pachete</h1></div>
                <button onClick={() => setEditing({ name: "", slug: "", description: "", image: "", price: 0, sale_price: null, items: [], includes_installation: true, active: true, featured: false })} className="btn-primary flex items-center gap-2"><Plus className="h-4 w-4" /> Adaugă</button>
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>{["Imagine", "Nume", "Produse", "Preț", "Instalare", "Activ", ""].map(h => <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}</tr></thead>
                    <tbody>
                        {items.map((p, i) => (
                            <tr key={p.package_id} className="border-t border-border" data-testid={`package-row-${i}`}>
                                <td className="px-3 py-2">{p.image ? <img src={MEDIA(p.image)} alt="" className="w-12 h-12 object-cover rounded" /> : "—"}</td>
                                <td className="px-3 py-2 font-medium">{p.name}</td>
                                <td className="px-3 py-2 text-xs">{p.items?.length || 0} produse</td>
                                <td className="px-3 py-2">{(p.sale_price || p.price)?.toFixed(2)}</td>
                                <td className="px-3 py-2">{p.includes_installation ? "✓" : "—"}</td>
                                <td className="px-3 py-2">{p.active ? "✓" : "—"}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => del(p.package_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-border"><h2 className="font-heading text-xl">{editing.package_id ? "Editează pachet" : "Pachet nou"}</h2><button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button></div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input placeholder="Nume *" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-soft" />
                                <input placeholder="Slug *" value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="input-soft" />
                                <textarea placeholder="Descriere" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows="3" className="input-soft md:col-span-2" />
                                <div className="md:col-span-2">
                                    <label className="text-xs text-muted-foreground block mb-1">Imagine</label>
                                    <div className="flex gap-2 items-center">
                                        {editing.image && <img src={MEDIA(editing.image)} alt="" className="w-16 h-16 object-cover rounded-lg" />}
                                        <input value={editing.image || ""} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="URL" className="input-soft flex-1" />
                                        <label className="btn-outline cursor-pointer text-sm"><Upload className="h-4 w-4" /><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} /></label>
                                    </div>
                                </div>
                                <input type="number" step="0.01" placeholder="Preț" value={editing.price ?? 0} onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })} className="input-soft" />
                                <input type="number" step="0.01" placeholder="Preț promoție" value={editing.sale_price ?? ""} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value === "" ? null : Number(e.target.value) })} className="input-soft" />
                                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.includes_installation} onChange={(e) => setEditing({ ...editing, includes_installation: e.target.checked })} />Instalare inclusă</label>
                                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />Activ</label>
                                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />Popular</label>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-heading text-lg">Produse în pachet</h3>
                                </div>
                                <ProductAutocomplete products={products} onAdd={(pid) => setEditing(p => {
                                    const existing = (p.items || []).find(x => x.product_id === pid);
                                    if (existing) return { ...p, items: p.items.map(x => x.product_id === pid ? { ...x, quantity: x.quantity + 1 } : x) };
                                    return { ...p, items: [...(p.items || []), { product_id: pid, quantity: 1 }] };
                                })} />
                                <div className="space-y-2 mt-3">
                                    {(editing.items || []).map((it, i) => {
                                        const prod = products.find(p => p.product_id === it.product_id);
                                        return (
                                            <div key={i} className="flex gap-2 items-center card-soft p-2" data-testid={`pkg-item-${i}`}>
                                                {prod?.images?.[0] && <img src={MEDIA(prod.images[0])} alt="" className="w-10 h-10 object-cover rounded-lg" />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{prod?.name || it.product_id}</div>
                                                    <div className="text-xs text-muted-foreground">{prod?.code} · {(prod?.sale_price || prod?.price || 0).toFixed(2)} RON</div>
                                                </div>
                                                <input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, "quantity", Number(e.target.value))} className="input-soft w-20 !py-2 text-sm" />
                                                <button onClick={() => removeItem(i)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn-outline">Anulează</button><button onClick={save} className="btn-primary" data-testid="package-save">Salvează</button></div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProductAutocomplete({ products, onAdd }) {
    const [q, setQ] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const matches = React.useMemo(() => {
        if (!q.trim()) return [];
        const needle = q.toLowerCase();
        return products.filter(p =>
            (p.name || "").toLowerCase().includes(needle) ||
            (p.code || "").toLowerCase().includes(needle) ||
            (p.brand || "").toLowerCase().includes(needle)
        ).slice(0, 8);
    }, [q, products]);

    return (
        <div className="relative">
            <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                   onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)}
                   placeholder="Caută produs după nume, cod sau brand..."
                   className="input-soft" data-testid="pkg-product-search" />
            {open && matches.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg z-20 max-h-72 overflow-y-auto" data-testid="pkg-autocomplete-list">
                    {matches.map(p => (
                        <button type="button" key={p.product_id}
                                onClick={() => { onAdd(p.product_id); setQ(""); setOpen(false); }}
                                className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2 border-b border-border/40 last:border-0"
                                data-testid={`pkg-autocomplete-${p.product_id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{p.name}</div>
                                <div className="text-xs text-muted-foreground">{p.code} · {p.brand}</div>
                            </div>
                            <div className="text-sm text-primary font-medium">{(p.sale_price || p.price || 0).toFixed(2)} RON</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
